import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs/operators';

import { VexToolbarDensity } from '@vex/interfaces/vex-route.interface';
import { checkRouterChildsData } from '@vex/utils/check-router-childs-data';

/**
 * The one place that answers "how tall is the secondary toolbar on this route".
 *
 * Two very different consumers need the same answer at the same instant:
 *  - {@link BaseLayoutComponent} puts `.vex-toolbar-compact` on the layout
 *    container, which is what actually changes the reserved height;
 *  - {@link VexSecondaryToolbarComponent} lays its own contents out on one row
 *    instead of two, and drops the crumb that would repeat the title.
 *
 * The bar cannot tell the layout: it is rendered inside the container's
 * `<ng-content>`, so anything it announced in `ngOnInit` would arrive a cycle
 * after the height class was evaluated — one frame at 76px, then a jump, on
 * every navigation between two densities. Reading the ROUTE gives both
 * consumers the value before the page is created, and derives it once.
 *
 * Absent `toolbarDensity` means `comfortable`. Declare it on the LEAF route:
 * `checkRouterChildsData` walks `route.data`, which Angular inherits, so a
 * value set on a component-less parent leaks to every child.
 */
@Injectable({ providedIn: 'root' })
export class ToolbarDensityService {
  private readonly router = inject(Router);

  readonly density$: Observable<VexToolbarDensity> = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith(null),
    map(() =>
      checkRouterChildsData(
        this.router.routerState.root.snapshot,
        (data) => data.toolbarDensity === 'compact',
      )
        ? ('compact' as const)
        : ('comfortable' as const),
    ),
    distinctUntilChanged(),
    // Every mounted toolbar plus the layout subscribe to this; without the
    // replay each subscriber would re-walk the route tree on its own and a
    // late subscriber (a bar created during the navigation) would see nothing
    // until the NEXT navigation.
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  readonly compact$: Observable<boolean> = this.density$.pipe(
    map((density) => density === 'compact'),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
}
